import React from "react";
import { Shield } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SectionToggle, NumInput } from "./shared";

const defaultTaxConfig = (currentSavings) => ({
  enabled: true,
  taxable_balance: Math.round(currentSavings * 0.2),
  tax_deferred_balance: Math.round(currentSavings * 0.6),
  roth_balance: Math.round(currentSavings * 0.2),
  ordinary_rate: 22,
  cap_gains_rate: 15,
  taxable_contrib_pct: 20,
  tax_deferred_contrib_pct: 60,
  roth_contrib_pct: 20,
});

export default function TaxConfigSection({ inputs, setInputs, open, onToggle }) {
  const taxOn = inputs.tax_config?.enabled;

  const updateTax = (k, v) => {
    setInputs({ ...inputs, tax_config: { ...inputs.tax_config, [k]: v } });
  };

  const toggleTax = (v) => {
    if (v && !inputs.tax_config) {
      setInputs({ ...inputs, tax_config: defaultTaxConfig(inputs.current_savings) });
    } else if (v) {
      updateTax("enabled", true);
    } else {
      updateTax("enabled", false);
    }
  };

  const contribSum = inputs.tax_config
    ? inputs.tax_config.taxable_contrib_pct + inputs.tax_config.tax_deferred_contrib_pct + inputs.tax_config.roth_contrib_pct
    : 100;

  return (
    <div>
      <SectionToggle
        testId="toggle-tax"
        icon={Shield}
        title="Tax-aware accounts"
        subtitle="Split balances across Taxable / 401k / Roth"
        badge={taxOn ? "on" : "off"}
        open={open}
        onToggle={onToggle}
      />
      {open && (
        <div className="pb-5 space-y-4">
          <div className="flex items-center justify-between text-sm">
            <Label className="text-[#1C2B23]">Enable tax-aware projections</Label>
            <Switch data-testid="tax-enabled" checked={!!taxOn} onCheckedChange={toggleTax} />
          </div>
          {taxOn && (
            <>
              <div className="grid grid-cols-1 gap-3">
                <NumInput id="tax-taxable" testid="tax-balance-taxable" label="Taxable balance" value={inputs.tax_config.taxable_balance} onChange={(v) => updateTax("taxable_balance", v)} prefix="$" step={1000} />
                <NumInput id="tax-taxdef" testid="tax-balance-taxdef" label="Tax-deferred (401k / IRA)" value={inputs.tax_config.tax_deferred_balance} onChange={(v) => updateTax("tax_deferred_balance", v)} prefix="$" step={1000} />
                <NumInput id="tax-roth" testid="tax-balance-roth" label="Roth balance" value={inputs.tax_config.roth_balance} onChange={(v) => updateTax("roth_balance", v)} prefix="$" step={1000} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <NumInput id="ord-rate" testid="tax-ordinary-rate" label="Ordinary income tax" value={inputs.tax_config.ordinary_rate} onChange={(v) => updateTax("ordinary_rate", v)} suffix="%" step={1} />
                <NumInput id="cg-rate" testid="tax-capgains-rate" label="Cap gains tax" value={inputs.tax_config.cap_gains_rate} onChange={(v) => updateTax("cap_gains_rate", v)} suffix="%" step={1} />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
                  Contribution split
                </Label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <NumInput id="c-taxable" testid="contrib-taxable" label="Taxable" value={inputs.tax_config.taxable_contrib_pct} onChange={(v) => updateTax("taxable_contrib_pct", v)} suffix="%" />
                  <NumInput id="c-taxdef" testid="contrib-taxdef" label="401k/IRA" value={inputs.tax_config.tax_deferred_contrib_pct} onChange={(v) => updateTax("tax_deferred_contrib_pct", v)} suffix="%" />
                  <NumInput id="c-roth" testid="contrib-roth" label="Roth" value={inputs.tax_config.roth_contrib_pct} onChange={(v) => updateTax("roth_contrib_pct", v)} suffix="%" />
                </div>
                {Math.abs(contribSum - 100) > 0.5 && (
                  <div className="text-xs text-[#C26747] mt-2">
                    Splits total {contribSum}% — adjust to 100%.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
